import {useEffect, useRef} from 'react';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {$generateNodesFromDOM} from '@lexical/html';
import {$getRoot, $insertNodes, LexicalNode} from 'lexical';
import {nodeType as styledTextNodeType} from '../nodes/StyledText';

interface Props {
    initialHtml?: string;
}

export const HtmlPlugin = ({initialHtml}: Props) => {
    const [editor] = useLexicalComposerContext();
    const isMounted = useRef(false);

    useEffect(() => {
        editor?.update(() => {
            if (!initialHtml || isMounted.current) return;
            isMounted.current = true;

            const parser = new DOMParser();
            const dom = parser.parseFromString(initialHtml, 'text/html');
            if (initialHtml.indexOf('<font') > 0) {
                const fontTags = dom.getElementsByTagName('font');
                unwrapFontTags(fontTags);
            }
            const nodes = $generateNodesFromDOM(editor, dom).filter(
                (item: LexicalNode, index: number) => {
                    const hasFirstEmptyLine = index === 0 && item.getTextContent() === '';
                    const isBlockNode =
                        item.getType() !== 'text' && item.getType() !== styledTextNodeType; // if some text node appears in the $root element, the editor will crash
                    return isBlockNode && !hasFirstEmptyLine;
                }
            );

            // Select the root
            $getRoot().select();
            // Insert them at a selection.
            $insertNodes(nodes);
            $getRoot().selectStart(); // select the start position of the editor
        });
    }, [editor, initialHtml]);

    return null;
};

function unwrapFontTags(fontTags: HTMLCollectionOf<Element>): void {
    for (let i = fontTags.length - 1; i >= 0; i--) {
        const fontTag = fontTags[i];
        const parent = fontTag.parentNode!;

        const color = fontTag.getAttribute('color');

        if (color) {
            const spanTag = document.createElement('span');
            spanTag.style.color = color;
            const childNodes = fontTag.childNodes;
            while (childNodes.length > 0) {
                const childNode = childNodes[0];
                fontTag.removeChild(childNode);
                spanTag.appendChild(childNode);
            }
            parent.replaceChild(spanTag, fontTag);
        } else {
            const childNodes = fontTag.childNodes;
            while (childNodes.length > 0) {
                const childNode = childNodes[0];
                parent.insertBefore(childNode, fontTag);
            }
            parent.removeChild(fontTag);
        }
    }
}
